/** @jsxImportSource @emotion/react */
import { faPencil, faSliders, faStop } from "@fortawesome/pro-regular-svg-icons";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Alert,
    FormGroup, Input,
    Modal, ModalBody, ModalFooter, ModalHeader
} from "reactstrap";

import { LAYER_TYPE } from "../../../../actions/Layers/constants";
import { applyDisplayFilterFields } from "../../../../actions/Tenants/config/applyDisplayFilter";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { useAddLayerAllocationMutation } from "../../../../hooks/mutations";
import { getFilledArrayOrDefault } from "../../../../utils/getFilledArrayOrDefault";
import MetaForm, { metaFormIsValid } from "../../../Forms/MetaForm";
import { RadioButtons } from "../../../Forms/RadioButton";
import SingleSelect from "../../../Forms/SingleSelect";
import { Button } from "../../../Helper/Button";
import useRipeningRoom, { useGetAllocatedLayers } from "../hooks/useRipeningRoom";
import RunningProgramEditor from "./RunningProgramEditor";
import { isEditableProgramCycle } from "./runningProgram";


const OVERDUE_DISMISSED_PARAM = "overdue_cycle_dismissed";

export default function EditRipeningCycleForm({ isInBottomBar }: { isInBottomBar?: boolean }) {
    const config = useConfig();
    const addAllocationMutation = useAddLayerAllocationMutation();
    const { fruit_type, currentLocation, activeRipeningCycle, refreshRipeningCycles, endRipeningProgram, endRipeningProgramLoading, editRipeningCycle, editRipeningCycleLoading, viewingActiveCycle, today, locations, refreshAllocatedLayers, allocatedLayersLoading, allocatedLayersError } = useRipeningRoom();
    const tenantId = config.tenant_id as string;
    const getAllocatedLayers = useGetAllocatedLayers();
    const [searchParams, setSearchParams] = useSearchParams();
    const dismissedCycleId = searchParams.get(OVERDUE_DISMISSED_PARAM);
    const [modalOpen, setModal] = useState(false);
    const [movePalletsModalOpen, setMovePalletsModalOpen] = useState(false);
    const [shouldMovePallets, setShouldMovePallets] = useState(false);
    const [newLocationId, setNewLocationId] = useState<string | null>(null);
    const [query, setQuery] = useState<any>({});
    const [isMovingPallets, setIsMovingPallets] = useState(false);

    const meta_form = applyDisplayFilterFields(config.root_config?.ripening_room_edit_cycle_form, { fruit_type });

    const activeCycleEndDateIsPast = useMemo(() => {
        if (!activeRipeningCycle?.ripening_end_date) {
            return false;
        }

        return today.startOf("day").isAfter(dayjs(activeRipeningCycle.ripening_end_date).startOf("day"));
    }, [activeRipeningCycle?.ripening_end_date, today]);

    // Initialize query with current location and active cycle values when modal opens
    useEffect(() => {

        if (modalOpen && (currentLocation || activeRipeningCycle)) {
            const initialQuery: any = {};

            // Initialize from active cycle if available
            if (activeRipeningCycle) {
                if (activeRipeningCycle.ripening_start_date) {
                    initialQuery.ripening_start_date = activeRipeningCycle.ripening_start_date;
                }
                if (activeRipeningCycle.ripening_end_date) {
                    initialQuery.ripening_end_date = activeRipeningCycle.ripening_end_date;
                }
                if (activeRipeningCycle.ripening_target !== undefined && activeRipeningCycle.ripening_target !== null) {
                    initialQuery.ripening_target = activeRipeningCycle.ripening_target;
                }
                if (activeRipeningCycle.season !== undefined && activeRipeningCycle.season !== null) {
                    initialQuery.season = activeRipeningCycle.season;
                }
            }

            // Initialize from current location for form fields
            if (currentLocation) {
                meta_form.forEach((field) => {
                    // Don't override cycle fields that were already set
                    if (initialQuery[field.name] === undefined && currentLocation[field.name] !== undefined) {
                        initialQuery[field.name] = currentLocation[field.name];
                    }
                });
            }

            setQuery(initialQuery);
        }
    }, [modalOpen, currentLocation?.location_id, activeRipeningCycle?.id, meta_form.map((i) => i.name).join(",")]);

    // Get currently allocated pallets
    const currentlyAllocatedPallets = useMemo(() => {
        return getAllocatedLayers(today, LAYER_TYPE.PALLET);
    }, [getAllocatedLayers, today]);

    const activeCycleRoomIsEmpty = useMemo(() => {
        if (!activeRipeningCycle?.id || allocatedLayersLoading || allocatedLayersError) {
            return false;
        }

        return currentlyAllocatedPallets.length === 0;
    }, [activeRipeningCycle?.id, allocatedLayersLoading, allocatedLayersError, currentlyAllocatedPallets.length]);

    const shouldAutoOpenEditModal = activeCycleEndDateIsPast || activeCycleRoomIsEmpty;

    useEffect(() => {
        if (!viewingActiveCycle || !activeRipeningCycle?.id || !shouldAutoOpenEditModal) {
            return;
        }

        if (dismissedCycleId === activeRipeningCycle.id) {
            return;
        }

        setModal(true);
    }, [viewingActiveCycle, activeRipeningCycle?.id, shouldAutoOpenEditModal, dismissedCycleId]);

    // Only show if viewing the active cycle
    if (!viewingActiveCycle) {
        return null;
    }

    const setValue = (field, value) => setQuery((prev) => ({ ...prev, [field]: value }));

    const onDebounce = (field, value) => {
        const field_config = meta_form.find((i) => i.name === field);
        if (metaFormIsValid([field_config], { [field]: value })) {
            return setQuery((prev) => ({ ...prev, [field]: value }));
        }
        return Promise.resolve();
    };

    const disableAllocationUI = config.root_config?.ripening_room_disable_allocation_ui;

    const onSubmitEdit = () => {
        if (meta_form && !metaFormIsValid(meta_form, query)) return;
        editRipeningCycle(query);
        setModal(false);
    };

    const onCloseEditModal = () => {
        // Mark this cycle as acknowledged so auto-open does not re-trigger after remounts.
        if (shouldAutoOpenEditModal && activeRipeningCycle?.id) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set(OVERDUE_DISMISSED_PARAM, activeRipeningCycle.id);
                return next;
            }, { replace: true });
        }
        setModal(false);
    };

    const onSubmitEnd = () => {
        if (meta_form && !metaFormIsValid(meta_form, query)) return;

        // If manual allocation is disabled, end the cycle immediately without showing the move pallets modal
        if (disableAllocationUI) {
            endRipeningProgram(query);
            setModal(false);
        } else {
            // Otherwise, show the move pallets modal
            setMovePalletsModalOpen(true);
        }
    };

    const onCloseMovePallets = () => {
        setMovePalletsModalOpen(false);
        setShouldMovePallets(false);
        setNewLocationId(null);
    };

    const allocatePalletsToLocation = (): Promise<void> => {
        if (!newLocationId || currentlyAllocatedPallets.length === 0) {
            return Promise.resolve();
        }

        if (!Array.isArray(locations)) {
            return Promise.resolve();
        }

        const location = locations.find((loc) => loc?.location_id === newLocationId);
        if (!location) {
            return Promise.resolve();
        }

        const { stage, location_id, facility } = location;
        const layerIds = currentlyAllocatedPallets.map((p) => p.id);

        return Promise.all(layerIds.map((layer_id: string) => addAllocationMutation.mutateAsync({
            tenantId,
            layerId: layer_id,
            stage,
            locationId: location_id,
            includeChildren: false,
            facility,
        }))).then(() => {
            refreshAllocatedLayers();
        });
    };

    const onConfirmMovePallets = () => {
        setIsMovingPallets(true);

        const movePalletsPromise = shouldMovePallets && newLocationId
            ? allocatePalletsToLocation()
            : Promise.resolve();

        movePalletsPromise
            .then(() => {
                // Always end the cycle after moving pallets (if needed)
                endRipeningProgram(query);
                onCloseMovePallets();
                setModal(false);
            })
            .finally(() => {
                setIsMovingPallets(false);
            });
    };

    // Transform locations for both RadioButtons and SingleSelect (both support label and value)
    const locationOptions = getFilledArrayOrDefault(locations)
        .filter((loc) => loc?.location_id !== currentLocation?.location_id)
        .map((loc) => ({
            ...loc,
            label: loc.label || loc.location_id,
            value: loc.location_id
        }));

    const layerCount = currentlyAllocatedPallets.length;
    const showProgram = !!config.root_config?.ripening_room_enable_ripewise && isEditableProgramCycle(activeRipeningCycle) && !!currentLocation?.location_id;

    const onProgramChanged = (result?: { ripening_end_date?: string }) => {
        // * The program change moved the end date: keep the cycle form in step so a later
        // * Submit does not send the old one back.
        if (result?.ripening_end_date) {
            setQuery((prev) => ({ ...prev, ripening_end_date: result.ripening_end_date }));
        }
        refreshRipeningCycles();
    };

    return <>
        {!disableAllocationUI && <Modal isOpen={movePalletsModalOpen} size="md">
            <ModalHeader>Move allocated pallets from cycle</ModalHeader>
            <ModalBody>
                <p className="mb-3">
                    Do you want to move all {layerCount} pallet{layerCount !== 1 ? "s" : ""} from this cycle to a different location?
                </p>
                <div className="mb-3">
                    <FormGroup check>
                        <Input
                            type="checkbox"
                            checked={shouldMovePallets}
                            onChange={(e) => {
                                setShouldMovePallets(e.target.checked);
                                if (!e.target.checked) {
                                    setNewLocationId(null);
                                }
                            }}
                        />
                        <label className="form-check-label">Move pallets to another location</label>
                    </FormGroup>
                </div>
                {shouldMovePallets && (
                    <div className="mt-3">
                        <label className="form-label">Select destination location:</label>
                        {locationOptions.length < 7 ? (
                            <RadioButtons
                                color="secondary"
                                name="location"
                                current={newLocationId || ""}
                                options={locationOptions}
                                onChange={(_, value) => setNewLocationId(value)}
                            />
                        ) : (
                            <SingleSelect
                                field="location_id"
                                options={locationOptions}
                                post_value={newLocationId ?? undefined}
                                setValue={(field, value) => setNewLocationId(value)}
                                label={false}
                            />
                        )}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="light" disabled={isMovingPallets} onClick={onCloseMovePallets}>Cancel</Button>
                <Button
                    color="warning"
                    isLoading={isMovingPallets}
                    onClick={onConfirmMovePallets}
                    disabled={(shouldMovePallets && !newLocationId) || isMovingPallets}
                >
                    End cycle{shouldMovePallets ? " and move pallets" : ""}
                </Button>
            </ModalFooter>
        </Modal>}

        <Modal isOpen={modalOpen} size={showProgram ? "xl" : "md"}>
            <ModalHeader >Edit ripening cycle</ModalHeader>
            <ModalBody className="px-4 py-3">
                {activeCycleEndDateIsPast && (
                    <Alert color="warning">
                        <div className="fw-semibold">This cycle is overdue.</div>
                        <div className="mb-0">The cycle end date is in the past. Do you want to end this cycle now?</div>
                    </Alert>
                )}
                {!activeCycleEndDateIsPast && activeCycleRoomIsEmpty && (
                    <Alert color="warning">
                        <div className="fw-semibold">This cycle has no pallets in the room.</div>
                        <div className="mb-0">All pallets have been moved out. Do you want to end this cycle now?</div>
                    </Alert>
                )}
                {showProgram && (
                    <div className="mb-4 pb-3 border-bottom">
                        <RunningProgramEditor
                            locationId={currentLocation.location_id}
                            cycle={activeRipeningCycle}
                            gatewayId={currentLocation?.gateway}
                            roomId={currentLocation?.controller_room}
                            tenantId={currentLocation?.tenant_id}
                            onChanged={onProgramChanged}
                        />
                    </div>
                )}
                {meta_form && meta_form.length > 0 ? (
                    <MetaForm
                        meta={meta_form}
                        object={query}
                        config={config}
                        setValue={setValue}
                        onDebounce={onDebounce}
                        extra_context={{ fruit_type: fruit_type || undefined }}
                    />
                ) : (
                    <p className="text-muted">No additional information required.</p>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={onCloseEditModal}>Cancel</Button>
                <Button color="success" icon={faPencil} onClick={onSubmitEdit} disabled={editRipeningCycleLoading || endRipeningProgramLoading}>Submit</Button>
                <Button color="warning" icon={faStop} onClick={onSubmitEnd} disabled={editRipeningCycleLoading || endRipeningProgramLoading}>End cycle</Button>
            </ModalFooter>
        </Modal>

        <Button className="text-nowrap" color="success" onClick={() => setModal(true)} icon={faSliders} isInBottomBar={isInBottomBar}>Edit cycle</Button>
    </>;

}

